import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {ColumnInstance, Row, useTable} from 'react-table'
import {useQuery, useQueryClient} from 'react-query'
import {Toast} from '../../../../_metronic/partials/widgets'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import ptbrLocale from '@fullcalendar/core/locales/pt-br'
import clsx from 'clsx'
import {ProjectCard} from '../../../../_metronic/partials/content/cards/ProjectCard'
import {RecurringProjectWidget} from '../../../../_metronic/partials/widgets'
import {
  useAuth,
  isInstanceOpenProjectLimitReached,
  INSTANCE_PROJECT_LIMIT_TOOLTIP,
} from '../../../modules/auth'
import {ContractsColumns} from './components/contracts-columns/_columns'
import {Loading} from '../../../../_metronic/partials/components/loading/Loading'
import {CustomRow} from './components/contracts-columns/CustomRow'
import {CustomHeaderColumn} from './components/contracts-columns/CustomHeaderColumn'
import {Contracts} from '../../../modules/contracts/components/contracts-list/core/_models'
import {getContractExpire} from '../../../modules/contracts/components/contracts-list/core/_requests'
import {ContractsPlaceholder} from '../../../modules/contracts/components/contracts-list/components/placeholder/ContractsPlaceholder'
import {
  getDashboardInfo,
  getDashboardTrends,
  getProjects,
  getRecurringProjects,
  getOpenedProjects,
  getRecentActivities,
  getTimeTrackingByTeamId,
  getTeams,
} from './core/_requests'
import {
  isNotEmpty,
  QUERIES,
  secondsToHoursFormatNotSeconds,
  diffInYears,
  diffInFullYears,
  UserAvatar,
  fullCalendarMobileDefaults,
  escapeHtml,
} from '../../../../_metronic/helpers'
import {RecentActivities} from './core/_models'
import {UpgradePlanWidget} from '../../../../_metronic/partials/widgets/_new/engage/UpgradePlanWidget'
import {ProjectCreateModalStepper} from '../../../../_metronic/partials/modals/project-create-modal-stepper/ProjectCreateModalStepper'
import {TaskModal} from '../../../../_metronic/partials/modals/task/TaskModal'
import {AIInsightsGrid} from './components/AIInsightsGrid'
import {OnboardingChecklist} from '../../../modules/onboarding/components/OnboardingChecklist'
import {useIsChecklistVisible} from '../../../modules/onboarding/hooks/useOnboardingProgress'
import {AttentionIndicatorsCarousel} from './components/AttentionIndicatorsCarousel'
import {DashboardTrendsCharts} from './components/DashboardTrendsSection'
import axios from 'axios'
import ManageCalendarsModal from '../../../modules/calendar/components/ManageCalendarsModal'
import {
  externalCalendarApiService,
  ExternalCalendarEvent,
} from '../../../modules/calendar/services/externalCalendarApi'
import {Collapse, Dropdown, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {UserFilter} from '../../../modules/calendar/components/UserFilter'

const DashboardManagerPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const intl = useIntl()
  const today = new Date()
  const [contracts, setContracts] = useState<Contracts[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentFilter, setCurrentFilter] = useState('0')
  const [showProjectsWithoutConsumption, setShowProjectsWithoutConsumption] = useState(false)
  const [calendarRange, setCalendarRange] = useState<{begin: string; end: string}>({
    begin: '',
    end: '',
  })
  const [showCompletedTasks, setShowCompletedTasks] = useState(false)
  const queryClient = useQueryClient()
  const {currentUser} = useAuth()
  // Enquanto o checklist de primeiros passos está visível, o banner de "criar primeiro projeto" sai.
  const checklistVisible = useIsChecklistVisible()
  const [calendarKey, setCalendarKey] = useState(0)
  const [showManageCalendarsModal, setShowManageCalendarsModal] = useState(false)
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)
  const [initialTaskName, setInitialTaskName] = useState<string>('')
  const [initialTaskDate, setInitialTaskDate] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  // Estados para seletor de equipe
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
  const [teams, setTeams] = useState<Array<{id: number; name: string}>>([])
  const initialRandomTeamSet = useRef(false)

  // Estados para filtros e ordenação de projetos
  const [projectSearchTerm, setProjectSearchTerm] = useState<string>('')
  const [projectStatusFilter, setProjectStatusFilter] = useState<string>('all')
  const [projectCriticalityFilter, setProjectCriticalityFilter] = useState<string>('all')
  const [projectMilestoneFilter, setProjectMilestoneFilter] = useState<string>('all')
  const [projectSortBy, setProjectSortBy] = useState<string>('criticality')
  const [projectsDisplayLimit, setProjectsDisplayLimit] = useState<number>(9)

  // Função para converter data YYYY-MM-DD para YYYY-MM-DD (manter formato ISO)
  const convertDateFormatToBR = (dateString: string): string => {
    if (!dateString || dateString === 'null' || dateString === 'undefined') {
      return ''
    }
    // Usar a string diretamente para evitar problemas de fuso horário
    // dateString já vem no formato YYYY-MM-DD do FullCalendar
    return dateString
  }

  // Função para atualizar expected_date
  const updateTaskExpectedDate = async (taskId: string, newDate: string) => {
    try {
      // Remover o prefixo "task-" do taskId se existir
      const cleanTaskId = taskId.startsWith('task-') ? taskId.substring(5) : taskId

      const url = `${process.env.REACT_APP_THEME_API_URL}/api/tarefas/calendar/${cleanTaskId}`
      const postData = {
        expected_date: newDate,
      }
      await axios.put(url, postData)

      // Invalidar cache para recarregar os dados
      queryClient.invalidateQueries(['dashboard-tasks'])

      return true
    } catch (error) {
      console.error('Erro ao atualizar data esperada da tarefa:', error)
      throw error
    }
  }

  // Handler para quando um evento é movido (drag and drop)
  const handleEventDrop = async (dropInfo: any) => {
    const eventId = dropInfo.event.id

    // TODO: Futuramente reativar verificação de administrador
    // Verificar se o usuário tem permissão para mover tarefas
    // if (!currentUser?.administrator) {
    //   dropInfo.revert()
    //   return
    // }

    // Verificar se é uma tarefa (não feriado ou férias)
    if (eventId.startsWith('holiday-') || eventId.startsWith('vacation-')) {
      // Reverter a mudança para feriados e férias
      dropInfo.revert()
      return
    }

    // Verificar se a nova data não é no passado (permitir data atual)
    const newDateStr = dropInfo.event.startStr // Formato: YYYY-MM-DD
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0] // Formato: YYYY-MM-DD

    if (newDateStr < todayStr) {
      // Reverter a mudança para datas passadas
      dropInfo.revert()
      Toast.fire({
        icon: 'warning',
        title: 'Não é possível mover tarefas para datas passadas.',
      })
      return
    }

    // Verificar se a tarefa está concluída
    const task = fetchedTasks.find((t: any) => String(t.id) === eventId)
    if (task && (task.status === 'Entregue' || task.status === 'Concluída')) {
      // Reverter a mudança para tarefas concluídas
      dropInfo.revert()
      alert('Tarefas concluídas não podem ser movidas.')
      return
    }

    try {
      // Converter a nova data para o formato esperado pela API (YYYY-MM-DD)
      const formattedDate = convertDateFormatToBR(dropInfo.event.startStr)

      // Atualizar a data esperada da tarefa
      await updateTaskExpectedDate(eventId, formattedDate)
    } catch (error) {
      // Reverter a mudança em caso de erro
      dropInfo.revert()
      console.error('Erro ao atualizar data da tarefa:', error)
      alert('Erro ao atualizar a data da tarefa. Tente novamente.')
    }
  }

  // Contratos: buscar só quando o módulo gestão de contratos estiver ativo (evita requisição desnecessária)
  useEffect(() => {
    const hasContractsModule = currentUser?.instance?.plan?.modules?.some(
      (m) => m.slug_name === 'gestao-de-contratos' && m.pivot?.active
    )
    if (!hasContractsModule || !currentUser) {
      setContracts([])
      setIsLoading(false)
      return
    }
    getContractExpire()
      .then((response) => {
        setContracts(response.data ?? [])
        setIsLoading(false)
      })
      .catch(() => setIsLoading(false))
  }, [currentUser])

  const contractsData = useMemo(() => contracts, [contracts])
  const columns = useMemo(() => ContractsColumns, [])
  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable({
    columns,
    data: contractsData,
  })

  const {data: dashboardInfo, isLoading: isLoadingDashboardInfo} = useQuery(
    `${QUERIES.DASHBOARD_INFO}`,
    () => {
      return getDashboardInfo()
    },
    {
      cacheTime: 0,
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  const {data: dashboardProjects, isLoading: isLoadingDashboardProjects} = useQuery(
    `${QUERIES.DASHBOARD_PROJECTS}`,
    () => getProjects({limit: 50}),
    {
      cacheTime: 0,
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  const {data: instanceTasksAttentionCounts} = useQuery(
    ['dashboard-instance-tasks-attention-counts'],
    async () => {
      const apiUrl = process.env.REACT_APP_THEME_API_URL

      const [criticalResponse, overdueResponse] = await Promise.all([
        axios.get(
          `${apiUrl}/api/instance-tasks?filter_status=aberta&filter_priority=critical&per_page=1`
        ),
        axios.get(
          `${apiUrl}/api/instance-tasks?filter_status=aberta&filter_only_overdue_tasks=true&per_page=1`
        ),
      ])

      const criticalTotal = criticalResponse?.data?.payload?.pagination?.total ?? 0
      const overdueTotal = overdueResponse?.data?.payload?.pagination?.total ?? 0

      return {
        criticalTotal,
        overdueTotal,
      }
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      onError: (err) => {
        console.error(err)
      },
    }
  )

  const {data: recurringProjects} = useQuery(
    `${QUERIES.DASHBOARD_RECURRING_PROJECTS}`,
    () => {
      return getRecurringProjects()
    },
    {
      cacheTime: 0,
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  const {data: openedProjects} = useQuery(
    `${QUERIES.DASHBOARD_OPENED_PROJECTS}`,
    () => {
      return getOpenedProjects()
    },
    {
      cacheTime: 0,
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  const [data, setData] = useState<RecentActivities[]>([])

  const {data: recentActivities} = useQuery(
    `${QUERIES.DASHBOARD_RECENT_ACTIVITIES}`,
    () => {
      return getRecentActivities()
    },
    {
      cacheTime: 0,
      onSuccess(response) {
        setData(response.data)
      },
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  // Buscar equipes disponíveis
  const {data: teamsData, isLoading: isLoadingTeams} = useQuery(
    ['dashboard-teams'],
    () => getTeams(),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        let uniqueTeams: Array<{id: number; name: string}> = []
        if (data?.teams && Array.isArray(data.teams)) {
          uniqueTeams = data.teams.filter(
            (
              team: {id: number; name: string},
              index: number,
              self: Array<{id: number; name: string}>
            ) => index === self.findIndex((t: {id: number; name: string}) => t.id === team.id)
          )
          setTeams(uniqueTeams)
        } else if (Array.isArray(data)) {
          uniqueTeams = data.filter(
            (
              team: {id: number; name: string},
              index: number,
              self: Array<{id: number; name: string}>
            ) => index === self.findIndex((t: {id: number; name: string}) => t.id === team.id)
          )
          setTeams(uniqueTeams)
        }
        // Ao carregar a tela pela primeira vez, selecionar equipe aleatória
        if (!initialRandomTeamSet.current && uniqueTeams.length > 0) {
          const randomTeam = uniqueTeams[Math.floor(Math.random() * uniqueTeams.length)]
          setSelectedTeamId(randomTeam.id)
          initialRandomTeamSet.current = true
        }
      },
      onError: (error) => {
        console.error('Error loading teams:', error)
      },
    }
  )

  // Query de time tracking - só executa se houver equipe selecionada
  const {data: dataConst} = useQuery(
    [`${QUERIES.TEAMS_LIST}-timetracking-dashboard-adm`, selectedTeamId, currentFilter],
    () => {
      // SEMPRE usar o endpoint com equipe específica - nunca usar métodos aleatórios
      if (selectedTeamId) {
        const period = currentFilter === '0' ? '7days' : '30days'
        return getTimeTrackingByTeamId(selectedTeamId, period)
      }
      // Retornar array vazio se não houver equipe selecionada
      return Promise.resolve({timeTrackingSquadRandomAdm: []})
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!selectedTeamId, // Só executar se houver equipe selecionada
    }
  )

  const [showMore, setShowMore] = useState(false)

  const handleEventClick = useCallback(
    (clickInfo: any) => {
      const eventId = clickInfo.event.id
      const eventTitle = clickInfo.event.title
      const isExternal = clickInfo.event.extendedProps?.external

      // Não faz nada para feriados ou férias
      if (eventId.startsWith('holiday-') || eventId.startsWith('vacation-')) {
        return
      }

      // Se for evento externo, abrir modal de criação de tarefa com título e data pré-preenchidos
      if (isExternal || eventId.startsWith('external_')) {
        setInitialTaskName(eventTitle)
        const eventDate = clickInfo.event.startStr.split('T')[0]
        setInitialTaskDate(eventDate)
        setShowCreateTaskModal(true)
        return
      }

      // Se for tarefa normal, abrir modal de edição
      if (clickInfo.event.extendedProps.taskId) {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('taskId', clickInfo.event.extendedProps.taskId.toString())
        setSearchParams(newParams)
      }
    },
    [searchParams, setSearchParams]
  )

  // useQuery para feriados
  const {data: holidays = []} = useQuery(
    ['dashboard-holidays'],
    async () => {
      const url = `${process.env.REACT_APP_THEME_API_URL}/api/holiday/`
      const response = await axios.get(url)
      return response.data.data || []
    },
    {refetchOnWindowFocus: true}
  )

  // useQuery para férias
  const {data: vacations = []} = useQuery(
    ['dashboard-vacations'],
    async () => {
      const url = `${process.env.REACT_APP_THEME_API_URL}/api/ferias/`
      const response = await axios.get(url)
      return response.data.data || []
    },
    {refetchOnWindowFocus: true}
  )

  // useQuery para tarefas
  const {data: fetchedTasks = []} = useQuery({
    queryKey: ['dashboard-tasks', calendarRange.begin, calendarRange.end, selectedUserId],
    queryFn: () => {
      if (!calendarRange.begin || !calendarRange.end) {
        return Promise.resolve([])
      }
      let url = `${process.env.REACT_APP_THEME_API_URL}/api/tarefas/calendar/${calendarRange.begin}/${calendarRange.end}`
      if (selectedUserId) {
        url += `?user_id=${selectedUserId}`
      }
      return axios.get(url).then((res) => res.data.data)
    },
    enabled: !!calendarRange.begin && !!calendarRange.end,
    refetchOnWindowFocus: true,
  })

  // useQuery para eventos externos (iCal) - apenas semana atual
  const {data: externalEvents = [], isLoading: isLoadingExternalEvents} = useQuery({
    queryKey: ['dashboard-external-calendar-events', calendarRange.begin, calendarRange.end],
    queryFn: () =>
      calendarRange.begin && calendarRange.end
        ? externalCalendarApiService.getEvents(calendarRange.begin, calendarRange.end)
        : Promise.resolve([]),
    enabled: !!calendarRange.begin && !!calendarRange.end,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Considerar dados válidos por 5 minutos
  })

  // Análise de tendências: consulta apenas quando o gestor clica em "Exibir análise"
  const [hasRequestedTrends, setHasRequestedTrends] = useState(false)
  const [isTrendsDetailsExpanded, setIsTrendsDetailsExpanded] = useState(true)
  const {
    data: trendsData,
    isLoading: isLoadingTrends,
    error: trendsError,
    refetch: refetchTrends,
  } = useQuery('dashboard-trends', getDashboardTrends, {
    enabled: hasRequestedTrends,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  })

  // Ao solicitar a análise, abrir o bloco automaticamente
  useEffect(() => {
    if (hasRequestedTrends) setIsTrendsDetailsExpanded(true)
  }, [hasRequestedTrends])

  const isIndicatorsSectionLoading = isLoadingDashboardInfo
  const isImmediateAttentionSectionLoading = isLoadingDashboardInfo || isLoadingDashboardProjects

  // Callback para quando um calendário externo for adicionado/atualizado
  const handleCalendarUpdated = useCallback(() => {
    queryClient.invalidateQueries(['dashboard-external-calendar-events'])
  }, [queryClient])

  // Função para converter data DD/MM/YYYY ou ISO para YYYY-MM-DD
  function convertDateFormat(dateString: string): string {
    if (!dateString || dateString === 'null' || dateString === 'undefined') {
      return ''
    }
    // ISO (ex: 2026-01-24T00:00:00.000000Z): usar os primeiros 10 caracteres
    if (
      dateString.includes('T') ||
      (dateString.length >= 10 && dateString.match(/^\d{4}-\d{2}-\d{2}/))
    ) {
      return dateString.substring(0, 10)
    }
    // Se já está no formato YYYY-MM-DD, retorna direto
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString
    }
    // Se está no formato DD/MM/YYYY, converte para YYYY-MM-DD
    const [day, month, year] = dateString.split('/')
    if (day && month && year) {
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
    return ''
  }

  // Efeito para forçar re-renderização do calendário quando os dados mudarem
  useEffect(() => {
    setCalendarKey((prev) => prev + 1)
  }, [fetchedTasks])

  // Listener para mudanças nas queries do calendário
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event && event.type === 'queryUpdated' && event.query.queryKey[0] === 'dashboard-tasks') {
        setCalendarKey((prev) => prev + 1)
      }
    })

    return () => unsubscribe()
  }, [queryClient])

  // Montar eventos do calendário (igual CalendarPage)
  const events = useMemo(() => {
    // Tasks
    const filteredTasks = fetchedTasks.filter((task: any) => {
      const isCompleted = task.status === 'Entregue' || task.status === 'Concluída'
      return showCompletedTasks || !isCompleted
    })
    const taskEvents = filteredTasks.map((task: any) => {
      const clientName = task.clientName || 'Cliente não definido'
      const projectName = task.project_name || 'Projeto não definido'
      // Nome, cliente e projeto vêm do usuário e o título vai para innerHTML: sempre escapar
      const htmlTitle = `<span class="fc-event-title-line fc-event-title-line--name"><strong>${escapeHtml(task.name)}</strong></span><span class="fc-event-title-line fc-event-title-line--meta">${escapeHtml(clientName)} / ${escapeHtml(projectName)}</span>`
      let eventDateString: string
      if (task.expected_date) {
        eventDateString = convertDateFormat(task.expected_date)
      } else if (task.created_at) {
        const createdDate = new Date(task.created_at)
        if (isNaN(createdDate.getTime())) {
          eventDateString = new Date().toISOString().split('T')[0]
        } else {
          eventDateString = createdDate.toISOString().split('T')[0]
        }
      } else {
        eventDateString = new Date().toISOString().split('T')[0]
      }
      // Lógica de tarefa vencida
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      const eventDate = new Date(eventDateString)
      const isOverdue = eventDate < yesterday
      const isInProgress = task.status === 'Em andamento'
      let eventClass = 'normal-task'
      if (isOverdue) {
        eventClass = 'overdue-task'
      } else if (isInProgress) {
        eventClass = 'in-progress-task'
      } else if (task.status === 'Entregue' || task.status === 'Concluída') {
        eventClass = 'completed-task'
      }
      return {
        id: String(task.id),
        title: task.name,
        start: eventDateString,
        allDay: true,
        classNames: [eventClass],
        editable: !(task.status === 'Entregue' || task.status === 'Concluída'), // Será controlado pelo FullCalendar
        extendedProps: {
          type: 'task',
          taskId: task.id,
          htmlTitle,
        },
      }
    })
    // Holidays
    const holidayEvents = holidays.map((holiday: any) => {
      const holidayDate = convertDateFormat(holiday.date)
      return {
        id: `holiday-${holiday.id}`,
        title: holiday.name,
        start: holidayDate,
        allDay: true,
        classNames: ['holiday-event'],
        display: 'background',
        backgroundColor: '#efa7a7',
        borderColor: '#efa7a7',
        editable: false, // Não permitir arrastar feriados
      }
    })
    // Vacations
    const vacationEvents = vacations.flatMap((vacation: any) => {
      let startDate, endDate
      if (vacation.begin_date.includes('-') && vacation.begin_date.split('-')[0].length === 4) {
        startDate = vacation.begin_date
      } else {
        startDate = convertDateFormat(vacation.begin_date)
      }
      if (vacation.end_date.includes('-') && vacation.end_date.split('-')[0].length === 4) {
        endDate = vacation.end_date
      } else {
        endDate = convertDateFormat(vacation.end_date)
      }
      // Gerar um evento para cada dia do período de férias
      const days: any[] = []
      let current = new Date(startDate)
      const last = new Date(endDate)
      while (current <= last) {
        const dayStr = current.toISOString().split('T')[0]
        days.push({
          id: `vacation-${vacation.id}-${dayStr}`,
          title: 'Férias',
          start: dayStr,
          allDay: true,
          display: 'background',
          classNames: ['vacation-event'],
          backgroundColor: '#efa7a7',
          borderColor: '#efa7a7',
          editable: false, // Não permitir arrastar férias
        })
        current.setDate(current.getDate() + 1)
      }
      return days
    })
    // Converte eventos externos para formato FullCalendar
    const externalCalendarEvents = externalEvents.map((event: ExternalCalendarEvent) => ({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      color: event.color,
      classNames: ['external-event'],
      editable: false,
      extendedProps: {
        calendarName: event.calendarName,
        calendarId: event.calendarId,
        external: true,
        description: event.description,
        location: event.location,
      },
    }))
    return [...taskEvents, ...holidayEvents, ...vacationEvents, ...externalCalendarEvents]
  }, [fetchedTasks, holidays, vacations, showCompletedTasks, currentUser, externalEvents])

  // Callback chamado sempre que a visão ou período do calendário muda
  const handleDatesSet = useCallback((arg: any) => {
    let begin_date = arg.startStr.substring(0, 10)
    let end_date = arg.endStr.substring(0, 10)
    setCalendarRange({begin: begin_date, end: end_date})
  }, [])

  const modules = currentUser?.instance?.plan?.modules
  const [showProjectModalStepper, setProjectShowModalStepper] = useState<boolean>(false)
  const [showTaskModalStepper, setTaskShowModalStepper] = useState<boolean>(false)

  const projectLimitReached = useMemo(
    () => isInstanceOpenProjectLimitReached(currentUser?.instance),
    [currentUser?.instance?.total_projects, currentUser?.instance?.open_projects_count]
  )

  const formatUserBirthdayDate = (date: string) => {
    const originalDate = new Date(date)

    const formatedDate =
      originalDate.getUTCDate().toString().padStart(2, '0') +
      '/' +
      (originalDate.getUTCMonth() + 1).toString().padStart(2, '0')

    return formatedDate
  }

  // Lógica de filtro e ordenação de projetos
  const filteredAndSortedProjects = useMemo(() => {
    if (!dashboardProjects?.projects) return []

    let filtered = [...dashboardProjects.projects]

    // Filtro por busca (nome ou cliente)
    if (projectSearchTerm) {
      const searchLower = projectSearchTerm.toLowerCase()
      filtered = filtered.filter(
        (project) =>
          project.name?.toLowerCase().includes(searchLower) ||
          project.clientName?.toLowerCase().includes(searchLower)
      )
    }

    // Filtro por status
    if (projectStatusFilter !== 'all') {
      const now = new Date()
      filtered = filtered.filter((project) => {
        const begin = project.begin_date ? new Date(project.begin_date) : null
        const end = project.end_date ? new Date(project.end_date) : null
        const closedAt = project.closed_at ? new Date(project.closed_at) : null

        switch (projectStatusFilter) {
          case 'not_started':
            return begin && now < begin
          case 'in_progress':
            return begin && end && now >= begin && now < end && !closedAt
          case 'delayed':
            return end && now > end && !closedAt
          case 'closed':
            return !!closedAt
          default:
            return true
        }
      })
    }

    // Filtro por criticidade
    if (projectCriticalityFilter !== 'all') {
      filtered = filtered.filter((project) => {
        const score = project.criticality_score ?? 0
        switch (projectCriticalityFilter) {
          case 'critical':
            return score >= 75
          case 'urgent':
            return score >= 50 && score < 75
          case 'attention':
            return score >= 25 && score < 50
          case 'normal':
            return score < 25
          default:
            return true
        }
      })
    }

    // Filtro por documentação (marcos)
    if (projectMilestoneFilter !== 'all') {
      const staleAfterDays = dashboardInfo?.stale_after_days ?? 14
      const now = new Date()
      filtered = filtered.filter((project) => {
        const lastAt = project.last_visible_milestone_at
        let daysSince: number | null = null
        if (lastAt) {
          const lastDate = new Date(`${lastAt}T00:00:00`)
          daysSince = Math.max(0, Math.floor((now.getTime() - lastDate.getTime()) / 86400000))
        }
        const isNever = daysSince === null
        const isStale = daysSince !== null && daysSince >= staleAfterDays
        const isUndocumented = isNever || isStale
        const score = project.criticality_score ?? 0

        switch (projectMilestoneFilter) {
          case 'priority':
            return score >= 50 && isUndocumented
          case 'unhealthy':
            return isUndocumented
          case 'never':
            return isNever
          case 'stale':
            return isStale
          default:
            return true
        }
      })
    }

    // Ordenação
    filtered.sort((a, b) => {
      switch (projectSortBy) {
        case 'criticality':
          return (b.criticality_score ?? 0) - (a.criticality_score ?? 0)
        case 'deadline':
          const aEnd = a.end_date ? new Date(a.end_date).getTime() : 0
          const bEnd = b.end_date ? new Date(b.end_date).getTime() : 0
          if (aEnd === 0) return 1
          if (bEnd === 0) return -1
          return aEnd - bEnd
        case 'profit_margin':
          return (b.profit_margin ?? 0) - (a.profit_margin ?? 0)
        case 'hours_consumed':
          return (b.totalTime ?? 0) - (a.totalTime ?? 0)
        case 'name':
          return (a.name || '').localeCompare(b.name || '')
        default:
          return 0
      }
    })

    return filtered
  }, [
    dashboardProjects?.projects,
    projectSearchTerm,
    projectStatusFilter,
    projectCriticalityFilter,
    projectMilestoneFilter,
    projectSortBy,
    dashboardInfo?.stale_after_days,
  ])

  // Projetos efetivamente exibidos (limitado para performance)
  const projectsToShow = useMemo(
    () => filteredAndSortedProjects.slice(0, projectsDisplayLimit),
    [filteredAndSortedProjects, projectsDisplayLimit]
  )

  const hasMoreProjects = filteredAndSortedProjects.length > projectsDisplayLimit

  // Resetar exibição ao mudar filtros/ordenação
  useEffect(() => {
    setProjectsDisplayLimit(9)
  }, [
    projectSearchTerm,
    projectStatusFilter,
    projectCriticalityFilter,
    projectMilestoneFilter,
    projectSortBy,
  ])

  return (
    <>
      {/* START HEADER - Título acima; 2 cards: RushMind | Análise de tendências */}
      <div className='container'>
        {/* Título: Workplace Destaque do dia + Boa tarde, [user] */}
        <div className='d-flex align-items-center gap-3 mb-4'>
          <div className='flex-shrink-0'>
            <UserAvatar
              name={currentUser?.name ?? ''}
              avatar={currentUser?.avatar}
              sizeClass='symbol-50px'
              className='overflow-hidden'
              imgClassName='object-fit-cover'
            />
          </div>
          <div>
            <h1 className='fs-2 fw-bold text-gray-900 mb-0'>
              {intl.formatMessage({id: 'DASHBOARD.LABEL.WORKPLACE'})}
              <span className='text-gray-500 fw-normal fs-4 ms-2'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.FEATURED_OF_THE_DAY'})}
              </span>
            </h1>
            <p className='text-gray-600 fs-6 mb-0 mt-1'>
              {today.getHours() < 12
                ? intl.formatMessage({id: 'GENERAL.GOOD_MORNING'})
                : today.getHours() < 18
                ? intl.formatMessage({id: 'GENERAL.GOOD_AFTERNOON'})
                : intl.formatMessage({id: 'GENERAL.GOOD_EVENING'})}
              , {currentUser?.name}!
            </p>
          </div>
        </div>

        {/* Banner CTA: criar primeiro projeto quando a instância não tem projetos */}
        {!isLoadingDashboardInfo && (dashboardInfo?.projects_quantity ?? 0) === 0 && !checklistVisible && (
          <div className='dash-empty-banner d-flex align-items-center mb-5' role='status'>
            <i className='bi bi-folder-plus dash-empty-banner-icon me-4'></i>
            <div className='d-flex flex-column flex-sm-row align-items-sm-center flex-grow-1'>
              <div className='mb-2 mb-sm-0'>
                <span className='fw-bold'>Ainda não há projetos na conta.</span>
                <span className='d-block d-sm-inline-block text-gray-700 ms-sm-2'>
                  Crie o primeiro projeto para começar a organizar tarefas e clientes.
                </span>
              </div>
              <div className='ms-sm-auto'>
                {projectLimitReached ? (
                  <OverlayTrigger
                    placement='top'
                    overlay={
                      <Tooltip id='dash-project-limit-banner'>
                        {INSTANCE_PROJECT_LIMIT_TOOLTIP}
                      </Tooltip>
                    }
                  >
                    <span
                      className='d-inline-block'
                      role='button'
                      tabIndex={0}
                      onClick={() => navigate('/billings')}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          navigate('/billings')
                        }
                      }}
                    >
                      <button type='button' className='btn btn-sm btn-primary dash-toolbar-btn-primary' disabled>
                        Criar primeiro projeto
                      </button>
                    </span>
                  </OverlayTrigger>
                ) : (
                  <button
                    type='button'
                    className='btn btn-sm btn-primary dash-toolbar-btn-primary'
                    onClick={() => setProjectShowModalStepper(true)}
                  >
                    Criar primeiro projeto
                  </button>
                )}
                <Link to='/projects' className='btn btn-sm btn-light ms-2'>
                  Ver projetos
                </Link>
              </div>
            </div>
          </div>
        )}

        <OnboardingChecklist />

        <div className='row g-4'>
          {/* Card esquerda (8 cols): RushMind */}
          <div className='col-12 col-lg-8'>
            <div className='dash-section h-100'>
              <AIInsightsGrid />
            </div>
          </div>

          {/* Card direita (4 cols): Análise de tendências com botão para exibir gráficos */}
          <div className='col-12 col-lg-4'>
            <div className='dash-section h-100'>
              <div className='d-flex flex-column h-100'>
                <div className='d-flex align-items-start'>
                  <i className='bi bi-graph-up-arrow text-gray-600 fs-2 me-3' />
                  <div>
                    <div className='fw-bold fs-4'>Análise de tendências</div>
                    <div className='text-muted fs-6 mt-1'>
                      Gráficos de margem, projetos concluídos, horas e Top 5 clientes.
                    </div>
                  </div>
                </div>
                <div className='flex-grow-1 d-flex align-items-center justify-content-center mt-4'>
                  {!hasRequestedTrends ? (
                    <button
                      type='button'
                      className='btn btn-primary'
                      onClick={() => setHasRequestedTrends(true)}
                    >
                      <i className='bi bi-graph-up-arrow me-2' />
                      Exibir análise
                    </button>
                  ) : isLoadingTrends ? (
                    <div className='d-flex align-items-center text-muted'>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      />
                      <span className='fs-6'>Carregando...</span>
                    </div>
                  ) : (
                    <button
                      type='button'
                      className='btn btn-light-primary btn-sm'
                      onClick={() => refetchTrends()}
                    >
                      <i className='bi bi-arrow-clockwise me-1' />
                      Atualizar
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bloco dos gráficos: exibido logo abaixo quando o gestor clica em "Exibir análise" */}
        {hasRequestedTrends && (
          <div className='dash-section mt-4'>
            <div className='row'>
              <div className='col-12'>
                <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4'>
                  <div className='d-flex align-items-center gap-2'>
                    <i className='bi bi-graph-up-arrow text-gray-600 fs-4' />
                    <div className='fw-bold fs-4'>Análise de tendências</div>
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={() => setIsTrendsDetailsExpanded((prev) => !prev)}
                    aria-expanded={isTrendsDetailsExpanded}
                    aria-controls='dashboard-trends-details'
                  >
                    <i
                      className={clsx(
                        'bi me-1',
                        isTrendsDetailsExpanded ? 'bi-chevron-up' : 'bi-chevron-down'
                      )}
                    />
                    {isTrendsDetailsExpanded ? 'Minimizar' : 'Exibir detalhes'}
                  </button>
                </div>
                <Collapse in={isTrendsDetailsExpanded}>
                  <div id='dashboard-trends-details'>
                    <DashboardTrendsCharts
                      trends={trendsData}
                      isLoading={isLoadingTrends}
                      error={trendsError}
                      refetch={refetchTrends}
                    />
                  </div>
                </Collapse>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* END HEADER */}
      <div className='container pt-8 pb-15'>
        {/* --------------------------------------------
            SEÇÃO 1: INDICADORES
            Métricas consolidadas e indicadores principais
        -------------------------------------------- */}
        <div className='row mb-10'>
          <div className='col-12'>
            <div className='dash-section-title'>
              <i className='bi bi-speedometer2 text-gray-600 me-2'></i>
              Indicadores
            </div>
          </div>
        </div>

        {/* KPIs: margem global, resumo financeiro, projetos abertos, atrasados, concluídos */}
        <div
          className={clsx(
            'dash-section tr-dashboard-section',
            isIndicatorsSectionLoading && 'tr-dashboard-section--loading'
          )}
        >
          {isIndicatorsSectionLoading && (
            <div className='tr-dashboard-section__overlay' aria-label='Carregando indicadores'>
              <div className='tr-dashboard-section__overlay-content'>
                <span className='spinner-border text-primary' role='status' aria-hidden='true' />
                <span className='fs-6 text-muted'>Carregando indicadores...</span>
              </div>
            </div>
          )}
          <div className='row g-4 tr-dashboard-section__content'>
            <div className='col-6 col-md-2 py-5 px-4 px-lg-5 text-center border-end border-gray-200'>
              <div className='d-flex align-items-center justify-content-center mb-2'>
                <i className='bi bi-percent text-primary fs-2 me-1'></i>
                <OverlayTrigger
                  placement='top'
                  delay={{show: 100, hide: 100}}
                  overlay={
                    <Tooltip id='margem-global-tooltip'>
                      <div className='text-start'>
                        <strong>Média das margens por projeto.</strong> Para cada projeto:
                        (orçamento − custo) ÷ orçamento × 100. O custo considera apenas horas de{' '}
                        <strong>responsáveis pela tarefa</strong> + custos extras. O resultado é a
                        média aritmética entre os projetos (cada projeto pesa igual). Pode diferir
                        da margem do Resumo Financeiro.
                      </div>
                    </Tooltip>
                  }
                >
                  <span className='cursor-pointer text-gray-500'>
                    <i className='bi bi-info-circle fs-6'></i>
                  </span>
                </OverlayTrigger>
              </div>
              <div className='fs-7 text-gray-600'>Margem global</div>
              <div className='fw-bold display-6 mt-1'>
                {dashboardInfo?.global_profit_margin != null
                  ? Math.floor(dashboardInfo.global_profit_margin)
                  : 0}
                %
              </div>
              {currentUser?.instance?.configuration?.min_profit_margin != null && (
                <div
                  className={clsx(
                    'fs-7 mt-1',
                    (dashboardInfo?.global_profit_margin ?? 0) >=
                      (currentUser?.instance?.configuration?.min_profit_margin ?? 0)
                      ? 'text-success'
                      : 'text-warning'
                  )}
                >
                  {(dashboardInfo?.global_profit_margin ?? 0) >=
                  (currentUser?.instance?.configuration?.min_profit_margin ?? 0)
                    ? 'Acima da margem'
                    : 'Abaixo da margem'}
                </div>
              )}
            </div>
            <div className='col-6 col-md-2 py-5 px-4 px-lg-5 text-center dash-indicator-border-end-md'>
              <div className='d-flex align-items-center justify-content-center mb-2'>
                <i className='bi bi-currency-dollar text-gray-600 fs-2 me-1'></i>
                <OverlayTrigger
                  placement='top'
                  delay={{show: 100, hide: 100}}
                  overlay={
                    <Tooltip id='resumo-financeiro-tooltip'>
                      <div className='text-start'>
                        <div className='mb-2'>
                          <strong>Valores acumulados (toda a base até hoje).</strong>
                        </div>
                        <strong>Base do cálculo</strong>
                        <ul className='mb-0 ps-3'>
                          <li>
                            <strong>Receita projetada:</strong> soma dos orçamentos dos projetos de
                            escopo fechado + soma dos orçamentos dos ciclos vigentes (projetos
                            recorrentes com ciclos até hoje).
                          </li>
                          <li>
                            <strong>Custo de horas:</strong> custo das horas lançadas no time
                            tracking (total_time × cost_hour por lançamento) + custos extras
                            cadastrados nos projetos.
                          </li>
                          <li>
                            <strong>Lucro:</strong> receita projetada − custo de horas.
                          </li>
                          <li>
                            <strong>Margem (Lucro ÷ Receita):</strong> ponderada pelo valor em R$
                            (projetos maiores pesam mais). Pode diferir da{' '}
                            <strong>Margem global</strong> (média por projeto; custo só de
                            responsáveis pela tarefa).
                          </li>
                        </ul>
                      </div>
                    </Tooltip>
                  }
                >
                  <span className='ms-1 cursor-pointer text-gray-500'>
                    <i className='bi bi-info-circle fs-6'></i>
                  </span>
                </OverlayTrigger>
              </div>
              <div className='fs-7 text-gray-600 mb-1'>Resumo Financeiro</div>
              <div className='d-flex flex-column gap-1 mt-1'>
                <div>
                  <span className='fs-7 text-gray-600'>Receita: </span>
                  <span className='fw-bold fs-6'>
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      maximumFractionDigits: 0,
                    }).format(dashboardInfo?.executive_summary?.revenue_planned ?? 0)}
                  </span>
                </div>
                <div>
                  <span className='fs-7 text-gray-600'>Custo: </span>
                  <span className='fw-bold fs-6'>
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      maximumFractionDigits: 0,
                    }).format(dashboardInfo?.executive_summary?.cost_hours ?? 0)}
                  </span>
                </div>
                <div>
                  <span className='fs-7 text-gray-600'>Lucro: </span>
                  <span
                    className={clsx(
                      'fw-bold fs-6',
                      (dashboardInfo?.executive_summary?.revenue_planned ?? 0) -
                        (dashboardInfo?.executive_summary?.cost_hours ?? 0) >=
                        0
                        ? 'text-success'
                        : 'text-danger'
                    )}
                  >
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      maximumFractionDigits: 0,
                    }).format(
                      (dashboardInfo?.executive_summary?.revenue_planned ?? 0) -
                        (dashboardInfo?.executive_summary?.cost_hours ?? 0)
                    )}
                  </span>
                </div>
              </div>
            </div>
            <Link
              to='/projects'
              className='col-6 col-md-2 py-5 px-4 px-lg-5 text-center border-end border-gray-200 text-decoration-none text-gray-800'
            >
              <i className='bi bi-folder2-open text-info fs-2 d-block mb-2'></i>
              <div className='fs-7 text-gray-600'>Projetos abertos</div>
              <div className='fw-bold display-6 mt-1'>
                {dashboardInfo?.open_projects_count ?? 0}
              </div>
            </Link>
            <Link
              to='/projects?status=late&byMe=0&orderBy=criticality&page=1'
              className='col-6 col-md-2 py-5 px-4 px-lg-5 text-center dash-indicator-border-end-md text-decoration-none text-gray-800'
            >
              <i className='bi bi-clock-history text-warning fs-2 d-block mb-2'></i>
              <div className='fs-7 text-gray-600'>Atrasados</div>
              <div className='fw-bold display-6 mt-1'>
                {dashboardInfo?.delayed_projects_count ?? 0}
              </div>
            </Link>
            <Link
              to='/projects?status=opened&byMe=0&milestone=priority&orderBy=criticality&page=1'
              className='col-6 col-md-2 py-5 px-4 px-lg-5 text-center border-end border-gray-200 text-decoration-none text-gray-800'
            >
              <div className='d-flex align-items-center justify-content-center mb-2'>
                <i className='bi bi-journal-check text-info fs-2 me-1'></i>
                <OverlayTrigger
                  placement='top'
                  delay={{show: 100, hide: 100}}
                  overlay={
                    <Tooltip id='marco-em-dia-tooltip'>
                      <div className='text-start'>
                        Porcentagem de projetos <strong>abertos</strong> com documentação recente. O
                        clique lista os <strong>prioritários</strong> (críticos/urgentes) sem
                        documentação atualizada.
                      </div>
                    </Tooltip>
                  }
                >
                  <span className='cursor-pointer text-gray-500'>
                    <i className='bi bi-info-circle fs-6'></i>
                  </span>
                </OverlayTrigger>
              </div>
              <div className='fs-7 text-gray-600'>Documentação em dia</div>
              <div className='fw-bold display-6 mt-1'>
                {dashboardInfo?.milestone_on_track_percent ?? 0}%
              </div>
            </Link>
            <div className='col-6 col-md-2 py-5 px-4 px-lg-5 text-center'>
              <i className='bi bi-check2-circle text-success fs-2 d-block mb-2'></i>
              <div className='fs-7 text-gray-600'>Concluídos</div>
              <div className='d-flex align-items-baseline justify-content-center flex-wrap flex-md-nowrap gap-1 mt-1'>
                <span className='fw-bold display-6'>
                  {dashboardInfo?.closed_projects_quantity ?? 0}
                </span>
                <span className='fw-normal fs-6 text-gray-600'>de</span>
                <span className='fw-normal fs-5 text-gray-600'>
                  {dashboardInfo?.projects_quantity ?? 0}
                </span>
                <span className='fw-normal fs-6 text-gray-500'>
                  (
                  {dashboardInfo?.closed_projects_percentage != null
                    ? Math.round(dashboardInfo.closed_projects_percentage)
                    : 0}
                  %)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* --------------------------------------------
            SEÇÃO 2: CRÍTICO / AÇÃO IMEDIATA
            Projetos com problemas, tarefas atrasadas, alertas urgentes
        -------------------------------------------- */}
        {/* Seção Atenção Imediata */}
        {(() => {
          // Filtrar projetos críticos (score >= 75) ou projetos atrasados
          const criticalProjects =
            dashboardProjects?.projects?.filter((project) => {
              const now = new Date()
              const endDate = project.end_date ? new Date(project.end_date) : null
              const isOverdue = endDate && now > endDate && !project.closed_at
              const isCritical = (project.criticality_score ?? 0) >= 75
              return isOverdue || isCritical
            }) || []

          const totalCriticalTasks =
            instanceTasksAttentionCounts?.criticalTotal ??
            (dashboardProjects?.projects?.reduce((sum, project) => {
              return sum + (project.critical_tasks_count ?? 0)
            }, 0) ||
              0)

          const userLateTasksCount =
            instanceTasksAttentionCounts?.overdueTotal ?? dashboardInfo?.user_late_tasks_count ?? 0
          const contractsExpiringSoonCount = dashboardInfo?.contracts_expiring_soon_count ?? 0
          const hasContractsModule = modules?.some(
            (m) => m.slug_name === 'gestao-de-contratos' && m.pivot?.active
          )
          const milestonePriorityCount = dashboardInfo?.milestone_priority_undocumented_count ?? 0
          const staleAfterDays = dashboardInfo?.stale_after_days ?? 14

          const shouldShowImmediateAttention =
            isLoadingDashboardInfo ||
            isLoadingDashboardProjects ||
            criticalProjects.length > 0 ||
            userLateTasksCount > 0 ||
            totalCriticalTasks > 0 ||
            (dashboardInfo?.over_budget_projects ?? 0) > 0 ||
            (hasContractsModule && contractsExpiringSoonCount > 0) ||
            milestonePriorityCount > 0

          if (!shouldShowImmediateAttention) {
            return null
          }

          const hasImmediateAttentionItems =
            criticalProjects.length > 0 ||
            userLateTasksCount > 0 ||
            totalCriticalTasks > 0 ||
            (dashboardInfo?.over_budget_projects ?? 0) > 0 ||
            (hasContractsModule && contractsExpiringSoonCount > 0) ||
            milestonePriorityCount > 0

          return (
            <div
              className={clsx(
                'dash-section tr-dashboard-section',
                isImmediateAttentionSectionLoading && 'tr-dashboard-section--loading',
                isImmediateAttentionSectionLoading &&
                  !hasImmediateAttentionItems &&
                  'tr-dashboard-section__min-height-sm'
              )}
            >
              {isImmediateAttentionSectionLoading && (
                <div
                  className='tr-dashboard-section__overlay'
                  aria-label='Carregando Atenção Imediata'
                >
                  <div className='tr-dashboard-section__overlay-content'>
                    <span
                      className='spinner-border text-primary'
                      role='status'
                      aria-hidden='true'
                    />
                    <span className='fs-6 text-muted'>Carregando atenção imediata...</span>
                  </div>
                </div>
              )}
              <div className='tr-dashboard-section__content'>
                <div className='dash-section-head'>
                  <div className='d-flex align-items-center'>
                    <i className='bi bi-exclamation-triangle text-warning fs-2 me-3'></i>
                    <div>
                      <div className='dash-section-title'>Atenção Imediata</div>
                      <div className='dash-section-subtitle'>Itens que requerem sua ação</div>
                    </div>
                  </div>
                </div>

                {hasImmediateAttentionItems && (
                  <AttentionIndicatorsCarousel>
                    {criticalProjects.length > 0 && (
                      <div className='card card-flush h-100'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center mb-3'>
                            <div className='symbol symbol-40px symbol-circle bg-light-warning me-3'>
                              <i className='bi bi-folder-fill text-warning fs-3'></i>
                            </div>
                            <div className='flex-grow-1'>
                              <div className='fw-bold text-gray-900 display-6'>
                                {criticalProjects.length}
                              </div>
                              <div className='text-gray-600 fs-7 fw-semibold d-flex align-items-center gap-1'>
                                {criticalProjects.length === 1
                                  ? 'Projeto crítico'
                                  : 'Projetos críticos'}
                                <OverlayTrigger
                                  placement='top'
                                  delay={{show: 100, hide: 100}}
                                  overlay={
                                    <Tooltip id='projetos-criticos-tooltip'>
                                      <div className='text-start'>
                                        Projetos com <strong>score de criticidade ≥ 75</strong> ou
                                        com <strong>data de fim já ultrapassada</strong> (em atraso)
                                        e ainda não concluídos.
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <span className='cursor-pointer text-gray-500'>
                                    <i className='bi bi-info-circle fs-6'></i>
                                  </span>
                                </OverlayTrigger>
                              </div>
                            </div>
                          </div>
                          <Link to='/projects' className='btn btn-sm btn-light-warning w-100'>
                            Ver projetos <i className='bi bi-arrow-right ms-1'></i>
                          </Link>
                        </div>
                      </div>
                    )}

                    {userLateTasksCount > 0 && (
                      <div className='card card-flush h-100'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center mb-3'>
                            <div className='symbol symbol-40px symbol-circle bg-light-danger me-3'>
                              <i className='bi bi-clock-history text-danger fs-3'></i>
                            </div>
                            <div className='flex-grow-1'>
                              <div className='fw-bold text-gray-900 display-6'>
                                {userLateTasksCount}
                              </div>
                              <div className='text-gray-600 fs-7 fw-semibold d-flex align-items-center gap-1'>
                                {userLateTasksCount === 1 ? 'Tarefa atrasada' : 'Tarefas atrasadas'}
                                <OverlayTrigger
                                  placement='top'
                                  delay={{show: 100, hide: 100}}
                                  overlay={
                                    <Tooltip id='tarefas-atrasadas-tooltip'>
                                      <div className='text-start'>
                                        Total de tarefas em atraso na{' '}
                                        <strong>instância inteira</strong> (todos os projetos e
                                        usuários), considerando tarefas com prazo vencido e ainda
                                        não concluídas.
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <span className='cursor-pointer text-gray-500'>
                                    <i className='bi bi-info-circle fs-6'></i>
                                  </span>
                                </OverlayTrigger>
                              </div>
                            </div>
                          </div>
                          <Link
                            to='/tarefas?overdue=true'
                            className='btn btn-sm btn-light-danger w-100'
                          >
                            Ver tarefas <i className='bi bi-arrow-right ms-1'></i>
                          </Link>
                        </div>
                      </div>
                    )}

                    {totalCriticalTasks > 0 && (
                      <div className='card card-flush h-100'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center mb-3'>
                            <div className='symbol symbol-40px symbol-circle bg-light-danger me-3'>
                              <i className='bi bi-exclamation-circle text-danger fs-3'></i>
                            </div>
                            <div className='flex-grow-1'>
                              <div className='fw-bold text-gray-900 display-6'>
                                {totalCriticalTasks}
                              </div>
                              <div className='text-gray-600 fs-7 fw-semibold d-flex align-items-center gap-1'>
                                {totalCriticalTasks === 1 ? 'Tarefa crítica' : 'Tarefas críticas'}
                                <OverlayTrigger
                                  placement='top'
                                  delay={{show: 100, hide: 100}}
                                  overlay={
                                    <Tooltip id='tarefas-criticas-tooltip'>
                                      <div className='text-start'>
                                        Soma das <strong>tarefas críticas</strong> da{' '}
                                        <strong>instância inteira</strong> (todos os projetos e
                                        usuários).
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <span className='cursor-pointer text-gray-500'>
                                    <i className='bi bi-info-circle fs-6'></i>
                                  </span>
                                </OverlayTrigger>
                              </div>
                            </div>
                          </div>
                          <Link
                            to='/tarefas?priority=critical'
                            className='btn btn-sm btn-light-danger w-100'
                          >
                            Ver tarefas <i className='bi bi-arrow-right ms-1'></i>
                          </Link>
                        </div>
                      </div>
                    )}

                    {(dashboardInfo?.over_budget_projects ?? 0) > 0 && (
                      <div className='card card-flush h-100'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center mb-3'>
                            <div className='symbol symbol-40px symbol-circle bg-light-danger me-3'>
                              <i className='bi bi-graph-down-arrow text-danger fs-3'></i>
                            </div>
                            <div className='flex-grow-1'>
                              <div className='fw-bold text-gray-900 display-6'>
                                {dashboardInfo?.over_budget_projects}
                              </div>
                              <div className='text-gray-600 fs-7 fw-semibold d-flex align-items-center gap-1'>
                                {dashboardInfo?.over_budget_projects === 1
                                  ? 'Projeto acima do orçamento'
                                  : 'Projetos acima do orçamento'}
                                <OverlayTrigger
                                  placement='top'
                                  delay={{show: 100, hide: 100}}
                                  overlay={
                                    <Tooltip id='overbudget-tooltip'>
                                      <div className='text-start'>
                                        Projetos em que o <strong>custo acumulado</strong> (horas
                                        apontadas com custo + custos extras) já ultrapassou o{' '}
                                        <strong>orçamento</strong>. O relatório detalha custo,
                                        margem e desvio.
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <span className='cursor-pointer text-gray-500'>
                                    <i className='bi bi-info-circle fs-6'></i>
                                  </span>
                                </OverlayTrigger>
                              </div>
                            </div>
                          </div>
                          <Link
                            to='/reports/overbudget-projects'
                            className='btn btn-sm btn-light-danger w-100'
                          >
                            Ver relatório <i className='bi bi-arrow-right ms-1'></i>
                          </Link>
                        </div>
                      </div>
                    )}

                    {hasContractsModule && contractsExpiringSoonCount > 0 && (
                      <div className='card card-flush h-100'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center mb-3'>
                            <div className='symbol symbol-40px symbol-circle bg-light-warning me-3'>
                              <i className='bi bi-file-earmark-text text-warning fs-3'></i>
                            </div>
                            <div className='flex-grow-1'>
                              <div className='fw-bold text-gray-900 display-6'>
                                {contractsExpiringSoonCount}
                              </div>
                              <div className='text-gray-600 fs-7 fw-semibold d-flex align-items-center gap-1'>
                                {contractsExpiringSoonCount === 1
                                  ? 'Contrato a vencer'
                                  : 'Contratos a vencer'}
                                <OverlayTrigger
                                  placement='top'
                                  delay={{show: 100, hide: 100}}
                                  overlay={
                                    <Tooltip id='contratos-tooltip'>
                                      <div className='text-start'>
                                        Contratos com <strong>vencimento em até 30 dias</strong> ou{' '}
                                        <strong>já vencidos</strong>, com base na data de fim do
                                        contrato.
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <span className='cursor-pointer text-gray-500'>
                                    <i className='bi bi-info-circle fs-6'></i>
                                  </span>
                                </OverlayTrigger>
                              </div>
                            </div>
                          </div>
                          <Link to='/contracts' className='btn btn-sm btn-light-warning w-100'>
                            Ver contratos <i className='bi bi-arrow-right ms-1'></i>
                          </Link>
                        </div>
                      </div>
                    )}

                    {milestonePriorityCount > 0 && (
                      <div className='card card-flush h-100'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center mb-3'>
                            <div className='symbol symbol-40px symbol-circle bg-light-warning me-3'>
                              <i className='bi bi-journal-text text-warning fs-3'></i>
                            </div>
                            <div className='flex-grow-1'>
                              <div className='fw-bold text-gray-900 display-6'>
                                {milestonePriorityCount}
                              </div>
                              <div className='text-gray-600 fs-7 fw-semibold d-flex align-items-center gap-1'>
                                {milestonePriorityCount === 1
                                  ? 'Prioritário sem documentação'
                                  : 'Prioritários sem documentação'}
                                <OverlayTrigger
                                  placement='top'
                                  delay={{show: 100, hide: 100}}
                                  overlay={
                                    <Tooltip id='marcos-priority-tooltip'>
                                      <div className='text-start'>
                                        Projetos <strong>críticos ou urgentes</strong> (score ≥ 50)
                                        sem marco recente (últimos{' '}
                                        <strong>{staleAfterDays} dias</strong>) ou sem marcos.
                                      </div>
                                    </Tooltip>
                                  }
                                >
                                  <span className='cursor-pointer text-gray-500'>
                                    <i className='bi bi-info-circle fs-6'></i>
                                  </span>
                                </OverlayTrigger>
                              </div>
                            </div>
                          </div>
                          <Link
                            to='/projects?status=opened&byMe=0&milestone=priority&orderBy=criticality&page=1'
                            className='btn btn-sm btn-light-warning w-100'
                          >
                            Ver projetos <i className='bi bi-arrow-right ms-1'></i>
                          </Link>
                        </div>
                      </div>
                    )}
                  </AttentionIndicatorsCarousel>
                )}
              </div>
            </div>
          )
        })()}

        {/* Calendário Semanal - Minha agenda da semana */}
        <div className='dash-section mt-10'>
          <div className='row'>
            <div className='col-12'>
              <div className='dash-section-head'>
                <div className='dash-section-title'>
                  <i className='bi bi-calendar-week text-gray-600 me-2'></i>
                  Agenda da semana
                </div>
                <div className='d-flex align-items-center flex-nowrap gap-2 dash-agenda-controls'>
                  {/* Filtro de usuários - somente para administradores e gestores */}
                  {Boolean(currentUser?.administrator || currentUser?.manager) && (
                    <div className='dash-agenda-controls__filter'>
                      <UserFilter
                        selectedUserId={selectedUserId}
                        onUserChange={(userId) => setSelectedUserId(userId)}
                        compact={true}
                      />
                    </div>
                  )}

                  {/* Indicador de loading para eventos externos */}
                  {isLoadingExternalEvents && (
                    <div className='me-3 d-flex align-items-center text-muted'>
                      <div className='spinner-border spinner-border-sm me-2' role='status'>
                        <span className='visually-hidden'>Carregando...</span>
                      </div>
                      <small>Sincronizando calendários...</small>
                    </div>
                  )}

                  <Dropdown>
                    <Dropdown.Toggle
                      variant='outline-info'
                      size='sm'
                      className='btn btn-sm btn-bg-light btn-active-color-info'
                    >
                      <i className='bi bi-calendar-plus me-2'></i>
                      Adicionar Agenda
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {/* Calendário iCal */}
                      <Dropdown.Item onClick={() => setShowManageCalendarsModal(true)}>
                        <div className='d-flex align-items-center'>
                          <i className='bi bi-calendar-plus fs-4 text-primary me-3'></i>
                          <div className='flex-grow-1'>
                            <div className='fw-bold'>Gerenciar Calendários</div>
                            <div className='text-muted fs-7'>Adicionar, remover ou editar</div>
                          </div>
                        </div>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <div className='dash-calendar-scroll'>
                <FullCalendar
                  key={calendarKey}
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView='dayGridWeek'
                  locale={ptbrLocale}
                  selectable={false}
                  eventClick={handleEventClick}
                  eventDrop={handleEventDrop}
                  editable={true}
                  {...fullCalendarMobileDefaults()}
                  headerToolbar={false}
                  height='300px'
                  events={events}
                  datesSet={handleDatesSet}
                  eventDisplay='block'
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: false,
                    hour12: false,
                  }}
                  eventDidMount={(info) => {
                    const titleElement = info.el.querySelector('.fc-event-title')
                    if (titleElement && info.event.extendedProps.htmlTitle) {
                      titleElement.innerHTML = info.event.extendedProps.htmlTitle
                    }

                    // Limpar formatação duplicada dos eventos externos (manter formato HH:MM)
                    const timeElement = info.el.querySelector('.fc-event-time') as HTMLElement
                    if (timeElement && info.event.extendedProps?.external) {
                      const timeText = timeElement.textContent || ''

                      // Se tem "h" duplicado, limpar e manter apenas o formato HH:MM
                      if (timeText.includes('h') && timeText.length > 6) {
                        const match = timeText.match(/^(\d{2}:\d{2})/)
                        if (match) {
                          timeElement.textContent = match[1]
                        }
                      }
                    }

                    // Adicionar cursor pointer para eventos externos
                    if (info.event.extendedProps?.external) {
                      info.el.style.cursor = 'pointer'
                    }

                    // Configurar atributos de arrastar
                    if (info.event.extendedProps.editable !== undefined) {
                      info.el.setAttribute(
                        'data-editable',
                        info.event.extendedProps.editable.toString()
                      )
                    }
                  }}
                />
              </div>

              <div className='mt-4'>
                <h6 className='mb-3'>Legenda:</h6>
                <div className='d-flex flex-wrap gap-4'>
                  <div className='d-flex align-items-center'>
                    <div className='legend-item overdue-task me-2'></div>
                    <span>Tarefas vencidas</span>
                  </div>
                  <div className='d-flex align-items-center'>
                    <div className='legend-item in-progress-task me-2'></div>
                    <span>Em andamento</span>
                  </div>
                  <div className='d-flex align-items-center'>
                    <div className='legend-item normal-task me-2'></div>
                    <span>Backlog / Outros</span>
                  </div>
                  <div className='d-flex align-items-center'>
                    <div className='legend-item holiday-event me-2'></div>
                    <span>Feriados ou Férias</span>
                  </div>
                  <div className='d-flex align-items-center'>
                    <div className='legend-item external-event me-2'></div>
                    <span>Calendários Externos</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {dashboardProjects?.projects.length !== 0 && (
          <div className='dash-section mt-10'>
            <div className='row g-6 g-xl-9'>
              <div className='col-12 mb-6'>
                <div className='dash-section-head'>
                  <div className='dash-section-title'>
                    <i className='bi bi-folder2 text-gray-600 me-2'></i>
                    {intl.formatMessage({id: 'DASHBOARD.LABEL.PROJECTS_I_LEAD'})}
                  </div>
                </div>

                {/* Controles de Filtro e Ordenação */}
                <div className='dash-filter-row mb-6'>
                  {/* Busca */}
                  <div className='position-relative dash-filter-search'>
                    <i className='bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-500'></i>
                    <input
                      type='text'
                      className='form-control form-control-sm ps-10'
                      placeholder='Buscar projeto ou cliente...'
                      value={projectSearchTerm}
                      onChange={(e) => setProjectSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Filtro por Status */}
                  <select
                    className='form-select form-select-sm'
                    value={projectStatusFilter}
                    onChange={(e) => setProjectStatusFilter(e.target.value)}
                  >
                    <option value='all'>Todos os status</option>
                    <option value='not_started'>Não iniciado</option>
                    <option value='in_progress'>Em andamento</option>
                    <option value='delayed'>Atrasado</option>
                    <option value='closed'>Concluído</option>
                  </select>

                  {/* Filtro por Criticidade */}
                  <select
                    className='form-select form-select-sm'
                    value={projectCriticalityFilter}
                    onChange={(e) => setProjectCriticalityFilter(e.target.value)}
                    title='Filtrar por nível de criticidade'
                  >
                    <option value='all'>Criticidade: todas</option>
                    <option value='critical'>Crítico</option>
                    <option value='urgent'>Urgente</option>
                    <option value='attention'>Atenção</option>
                    <option value='normal'>Normal</option>
                  </select>

                  {/* Filtro por documentação */}
                  <select
                    className='form-select form-select-sm'
                    value={projectMilestoneFilter}
                    onChange={(e) => setProjectMilestoneFilter(e.target.value)}
                    title='Documentação por marcos'
                  >
                    <option value='all'>Documentação: todas</option>
                    <option value='priority'>Prioritários sem documentação</option>
                    <option value='unhealthy'>Sem documentação recente</option>
                    <option value='never'>Sem marcos</option>
                    <option value='stale'>Documentação desatualizada</option>
                  </select>

                  {/* Ordenação */}
                  <select
                    className='form-select form-select-sm'
                    value={projectSortBy}
                    onChange={(e) => setProjectSortBy(e.target.value)}
                    title='Ordenar lista de projetos'
                  >
                    <option value='criticality'>Ordenar: criticidade</option>
                    <option value='deadline'>Ordenar: prazo</option>
                    <option value='profit_margin'>Ordenar: margem</option>
                    <option value='hours_consumed'>Ordenar: horas</option>
                    <option value='name'>Ordenar: nome</option>
                  </select>
                </div>

                {/* Contador de resultados */}
                <div className='mb-4'>
                  <span className='text-gray-600 fs-7'>
                    Mostrando {projectsToShow.length} de {filteredAndSortedProjects.length} projeto
                    {filteredAndSortedProjects.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Lista de Projetos Filtrados */}
              {filteredAndSortedProjects.length > 0 ? (
                <>
                  {projectsToShow.map((project, i) => (
                    <div key={project.id ?? i} className='col-md-6 col-lg-4'>
                      <ProjectCard
                        id={parseInt(project.id!)}
                        name={project.name}
                        client={project.clientName}
                        budgetedHours={project.budgeted_hours}
                        users={project.users!}
                        begin_date={project.begin_date}
                        end_date={project.end_date}
                        closed_at={project.closed_at}
                        totalTime={project.totalTime}
                        profit_margin={project.profit_margin}
                        is_private={project.is_private}
                        criticality_score={project.criticality_score}
                        critical_tasks_count={project.critical_tasks_count}
                        late_tasks_count={project.late_tasks_count}
                        high_priority_tasks_count={project.high_priority_tasks_count}
                        overdue_1_3_tasks_count={project.overdue_1_3_tasks_count}
                        overdue_4_7_tasks_count={project.overdue_4_7_tasks_count}
                        overdue_8_plus_tasks_count={project.overdue_8_plus_tasks_count}
                        due_soon_high_critical_tasks_count={
                          project.due_soon_high_critical_tasks_count
                        }
                        total_tasks={project.total_tasks}
                      />
                    </div>
                  ))}
                  {hasMoreProjects && (
                    <div className='col-12 d-flex justify-content-center mt-4'>
                      <button
                        type='button'
                        className='btn btn-light-primary'
                        onClick={() => setProjectsDisplayLimit((prev) => prev + 9)}
                      >
                        Carregar mais
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className='col-12'>
                  <div className='text-center py-10'>
                    <i className='bi bi-inbox fs-3x text-gray-400 mb-4'></i>
                    <div className='fs-4 fw-bold text-gray-700 mb-2'>Nenhum projeto encontrado</div>
                    <div className='text-gray-600 fs-6'>
                      Tente ajustar os filtros ou limpar a busca para ver mais resultados.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className='dash-section'>
          <div className='row'>
            <div className='col-lg-6 pb-10 pb-lg-0 mb-10 mb-lg-0 border-bottom border-bottom-lg-0'>
              <div className='dash-section-title mb-8'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.HR_HIGHLIGHTS'})}
              </div>
              <div className='content'>
                <div
                  className={`resume overflow-hidden transition-all ${
                    showMore ? 'mh-300px overflow-scroll' : 'mh-210px'
                  }`}
                >
                  {dashboardInfo?.hr_highlights.length === 0 && (
                    <p className='dash-empty-text'>
                      Aniversários e tempo de casa da equipe aparecem aqui.
                    </p>
                  )}
                  {dashboardInfo?.hr_highlights.map((user, i) => (
                    <div key={i} className='py-5 border-gray-200 d-flex align-items-center'>
                      <div className='d-flex align-items-center'>
                        <div>
                          <UserAvatar
                            name={user.name}
                            avatar={user.avatar}
                            sizeClass='symbol-40px'
                            imgClassName='object-fit-cover'
                          />
                        </div>

                        <div className='ms-4'>
                          <a className='fs-6 fw-bold text-gray-900 text-hover-primary mb-2'>
                            {user.name}
                          </a>
                          <div className='fw-semibold fs-7 text-muted'>
                            {user.type === 'aniversario_nascimento' &&
                              'Aniversário - ' + formatUserBirthdayDate(user.date)}
                            {user.type === 'aniversario_empresa' &&
                              `Em ${formatUserBirthdayDate(user.date)} faz ${diffInFullYears(
                                user.date,
                                today.toString()
                              )} ${
                                diffInYears(user.date, today.toString()) === 1 ? 'ano' : 'anos'
                              } de empresa`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {dashboardInfo?.hr_highlights?.length! > 3 && (
                  <div className='read-more text-center text-gray-200 mt-4'>
                    <span
                      className='text-primary cursor-pointer'
                      onClick={() => {
                        setShowMore(!showMore)
                      }}
                    >
                      {showMore
                        ? intl.formatMessage({id: 'GENERAL.SEE_LESS'})
                        : intl.formatMessage({id: 'GENERAL.SEE_MORE'})}
                      <i className='bi bi-chevron-expand text-primary'></i>
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className='col-lg-6'>
              <div className='card card-flush '>
                <div className='card-header d-flex align-items-center p-1 justify-content-between flex-wrap gap-2'>
                  <h3 className='card-title m-0 fs-2 fw-bold'>
                    {intl.formatMessage({id: 'DASHBOARD.LABEL.TEAM_REGISTERED_HOURS'})}
                    {selectedTeamId &&
                      (() => {
                        const name = teams.find((t) => t.id === selectedTeamId)?.name
                        return name ? ` [${name}]` : ''
                      })()}
                  </h3>
                  <div className='d-flex align-items-center gap-2'>
                    <select
                      className='form-select form-select-sm'
                      style={{minWidth: '160px'}}
                      value={selectedTeamId || ''}
                      onChange={(e) =>
                        setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)
                      }
                      disabled={isLoadingTeams}
                    >
                      <option value=''>
                        {isLoadingTeams ? 'Carregando...' : 'Selecione uma equipe'}
                      </option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                    <select
                      className='form-select form-select-sm'
                      style={{minWidth: '130px'}}
                      value={currentFilter}
                      onChange={(e) => setCurrentFilter(e.target.value)}
                    >
                      <option value='0'>Últimos 7 dias</option>
                      <option value='1'>Últimos 30 dias</option>
                    </select>
                  </div>
                </div>
                <div className='card-body pt-6'>
                  <div className='tab-content'>
                    <div
                      className='tab-pane fade show active'
                      id='kt_stats_widget_16_tab_1'
                      role='tabpanel'
                      aria-labelledby='#kt_stats_widget_16_tab_link_1'
                    >
                      <div className='table-responsive'>
                        <table className='table table-row-dashed align-middle gs-0 gy-3 my-0'>
                          <thead>
                            <tr className='fs-7 fw-bold text-gray-400 border-bottom-0'>
                              <th className='p-0 pb-3 min-w-150px text-start'>MEMBRO</th>
                              <th className='p-0 pb-3 min-w-100px text-end pe-13 text-center'>
                                ÍNDICE DE LANÇAMENTO
                              </th>
                              <th className='p-0 pb-3 w-125px text-end pe-7'>ALOCAÇÃO</th>
                            </tr>
                          </thead>

                          <tbody>
                            {!selectedTeamId ? (
                              <tr>
                                <td colSpan={3} className='text-center'>
                                  <span className='dash-empty-text fw-semibold'>
                                    Selecione uma equipe para visualizar os dados
                                  </span>
                                </td>
                              </tr>
                            ) : dataConst?.timeTrackingSquadRandomAdm?.length === 0 ? (
                              <tr>
                                <td colSpan={3} className='text-center'>
                                  <span className='dash-empty-text fw-semibold'>
                                    Nenhum apontamento realizado até o momento...
                                  </span>
                                </td>
                              </tr>
                            ) : (
                              dataConst?.timeTrackingSquadRandomAdm.map((row, i) => (
                                <tr key={row.id}>
                                  <td>
                                    <div className='d-flex align-items-center'>
                                      <div className='me-3'>
                                        <UserAvatar
                                          name={row.name}
                                          avatar={row.avatar}
                                          sizeClass='symbol-40px'
                                          className='overflow-hidden'
                                          imgClassName='object-fit-cover'
                                        />
                                      </div>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <a
                                          href=''
                                          className='text-gray-800 fw-bold text-hover-primary mb-1 fs-6'
                                        >
                                          {row.name}
                                        </a>
                                        <span className='text-gray-400 fw-semibold d-block fs-7'>
                                          {row.job_role}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className='text-end pe-13 text-center'>
                                    <span
                                      className={`fw-bold fs-6 text-center ${
                                        (row!.performance! >=
                                          row.instanceConfiguration.min_hour_index &&
                                          row!.performance! <=
                                            row.instanceConfiguration.max_hour_index) ||
                                        !row.has_time_tracking
                                          ? 'text-success'
                                          : row!.performance! <
                                            row.instanceConfiguration.min_hour_index
                                          ? 'text-danger'
                                          : 'text-warning'
                                      }`}
                                    >
                                      {isNotEmpty(row.performance) ? row.performance + '%' : '-'}
                                    </span>
                                  </td>
                                  <td className='text-end pe-0 text-center'>
                                    <span className='text-gray-600 fw-bold fs-6 text-center'>
                                      <span
                                        className={`${
                                          (row!.performance! >=
                                            row.instanceConfiguration.min_hour_index &&
                                            row!.performance! <=
                                              row.instanceConfiguration.max_hour_index) ||
                                          !row.has_time_tracking
                                            ? 'text-success'
                                            : row!.performance! <
                                              row.instanceConfiguration.min_hour_index
                                            ? 'text-danger'
                                            : 'text-warning'
                                        }`}
                                      >
                                        {row?.hours_tracked}h
                                      </span>{' '}
                                      de {secondsToHoursFormatNotSeconds(row?.hours_total!)}h
                                    </span>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='dash-section'>
          <div className='row align-items-end'>
            <div className='dash-section-head'>
              <div className='dash-section-title'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.RECURRING_PROJECTS'})}
              </div>
              <div className='d-flex align-items-center'>
                <div className='form-check form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='showProjectsWithoutConsumption'
                    checked={showProjectsWithoutConsumption}
                    onChange={(e) => setShowProjectsWithoutConsumption(e.target.checked)}
                  />
                  <label
                    className='form-check-label fs-6 text-gray-700'
                    htmlFor='showProjectsWithoutConsumption'
                  >
                    Exibir projetos sem consumo
                  </label>
                </div>
              </div>
            </div>
            {recurringProjects?.projects.length === 0 ||
            recurringProjects?.projects.filter((project) => {
              if (showProjectsWithoutConsumption) {
                return true
              }
              return (project.total_time_percentage || 0) > 0
            }).length === 0 ? (
              <div className='d-flex flex-column justify-content-center'>
                <div className='mb-10'>
                  <div className='dash-empty-text text-center mb-6'>
                    <p className='me-2'>
                      {recurringProjects?.projects.length === 0
                        ? 'Você ainda não tem projetos recorrentes. Crie seus primeiros para acompanhar com facilidade!'
                        : 'Não há projetos recorrentes com consumo de tempo no momento. Marque a opção "Exibir projetos sem consumo" para ver todos os projetos.'}
                    </p>
                  </div>

                  <div className='text-center'>
                    {projectLimitReached ? (
                      <OverlayTrigger
                        placement='top'
                        overlay={
                          <Tooltip id='dash-project-limit-recurring'>
                            {INSTANCE_PROJECT_LIMIT_TOOLTIP}
                          </Tooltip>
                        }
                      >
                        <span
                          className='d-inline-block'
                          role='button'
                          tabIndex={0}
                          onClick={() => navigate('/billings')}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              navigate('/billings')
                            }
                          }}
                        >
                          <button type='button' className='btn btn-sm btn-primary dash-toolbar-btn-primary' disabled>
                            Novo projeto
                          </button>
                        </span>
                      </OverlayTrigger>
                    ) : (
                      <button
                        type='button'
                        onClick={() => setProjectShowModalStepper(true)}
                        className='btn btn-sm btn-primary dash-toolbar-btn-primary'
                      >
                        Novo projeto
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className='row g-6'>
                {recurringProjects?.projects
                  .filter((project) => {
                    // Se o filtro estiver ativo, mostra todos os projetos
                    if (showProjectsWithoutConsumption) {
                      return true
                    }
                    // Se não estiver ativo, só mostra projetos com consumo (total_time_percentage > 0)
                    return (project.total_time_percentage || 0) > 0
                  })
                  .map((project, i) => (
                    <div key={i} className='col-12 col-md-6 col-lg-4 col-xl-3'>
                      <RecurringProjectWidget project={project} />
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className='dash-section'>
          <div className='row justify-content-between'>
            <div className='col-lg-6 pb-10 pb-lg-0 mb-15 mb-lg-0 border-bottom border-bottom-lg-0'>
              <div className='dash-section-title mb-8'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.PROGRESS_OPEN_PROJECTS'})}
              </div>
              <div className='mh-350px overflow-x-hidden overflow-y-scroll'>
                {openedProjects?.projects.length === 0 ? (
                  <div className='d-flex flex-column justify-content-center'>
                    <div className='mb-10'>
                      <div className='dash-empty-text text-center mb-6'>
                        <p className='me-2'>
                          Sem projetos em aberto no momento. Inicie um novo projeto para acompanhar
                          o progresso!
                        </p>
                      </div>

                      <div className='text-center'>
                        {projectLimitReached ? (
                          <OverlayTrigger
                            placement='top'
                            overlay={
                              <Tooltip id='dash-project-limit-opened'>
                                {INSTANCE_PROJECT_LIMIT_TOOLTIP}
                              </Tooltip>
                            }
                          >
                            <span
                              className='d-inline-block'
                              role='button'
                              tabIndex={0}
                              onClick={() => navigate('/billings')}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  navigate('/billings')
                                }
                              }}
                            >
                              <button type='button' className='btn btn-sm btn-primary dash-toolbar-btn-primary' disabled>
                                Novo projeto
                              </button>
                            </span>
                          </OverlayTrigger>
                        ) : (
                          <button
                            type='button'
                            onClick={() => setProjectShowModalStepper(true)}
                            className='btn btn-sm btn-primary dash-toolbar-btn-primary'
                          >
                            Novo projeto
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  openedProjects?.projects
                    .sort((a, b) => (b.used_time || 0) - (a.used_time || 0))
                    .map((openProject, i) => (
                      <div key={i} className='mb-3'>
                        <div className='d-flex flex-column'>
                          <Link
                            to={`/clients/${openProject.client_id}`}
                            target='_blank'
                            className='text-dark'
                          >
                            {openProject.clientName}
                          </Link>
                          <Link
                            to={`/projects/${openProject.id}`}
                            target='_blank'
                            className='text-dark'
                          >
                            {openProject.name}
                          </Link>
                        </div>

                        <div className='d-flex align-items-center'>
                          <div className='col-9'>
                            <div
                              className='h-7px w-100 bg-light overflow-hidden rounded'
                              data-bs-toggle='tooltip'
                            >
                              <div
                                className={clsx(
                                  'rounded h-7px',
                                  openProject.used_time! >= 100
                                    ? 'bg-danger'
                                    : openProject.used_time! > 80
                                    ? 'bg-success'
                                    : 'bg-primary'
                                )}
                                role='progressbar'
                                style={{width: `${openProject.used_time}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className='col-3 text-start ps-2'>{`${Math.round(
                            openProject.used_time!
                          )}%`}</div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
            <div className='col-lg-6'>
              <div className='dash-section-title mb-8'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.RELEVANT_ACTIVITIES'})}
              </div>
              {data?.length === 0 ? (
                <div className='d-flex flex-column justify-content-center'>
                  <div className='mb-10'>
                    <div className='dash-empty-text text-center mb-6'>
                      <p className='me-2'>
                        Nenhuma atividade por aqui ainda. Comece a registrar suas tarefas e
                        mantenha-se organizado!
                      </p>
                    </div>

                    <div className='text-center'>
                      <button
                        onClick={() => setTaskShowModalStepper(true)}
                        className='btn btn-sm btn-primary dash-toolbar-btn-primary'
                      >
                        Nova tarefa
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='border border-gray-200 py-2 px-5 h-350px overflow-x-hidden overflow-y-scroll'>
                  {data?.map((recentActivity, index) => (
                    <div
                      key={index}
                      className='py-5 border-bottom border-gray-200 d-flex align-items-center'
                    >
                      <div className='me-5'>
                        <UserAvatar
                          name={recentActivity.userName ?? ''}
                          avatar={recentActivity.userAvatar}
                          sizeClass='symbol-30px'
                          imgClassName='object-fit-cover'
                        />
                      </div>
                      <div>
                        {recentActivity.task_id ? (
                          <>
                            {recentActivity.userName} -{' '}
                            <span className='text-gray fw-bold'>
                              {' '}
                              {recentActivity.description} - {recentActivity.created_at}{' '}
                            </span>
                            <span
                              className='text-primary cursor-pointer'
                              onClick={(e) => {
                                const newParams = new URLSearchParams(searchParams)
                                newParams.set('taskId', String(recentActivity.task_id))
                                setSearchParams(newParams)
                              }}
                            >
                              {' '}
                              #{recentActivity.task_id}
                            </span>
                          </>
                        ) : (
                          <>
                            {recentActivity.userName} -{' '}
                            <span className='text-gray fw-bold'> {recentActivity.description}</span>
                            <span className='text-lowercase'>
                              {' '}
                              {intl.formatMessage({id: 'GENERAL.THE_PROJECT'})}{' '}
                            </span>
                            <Link to={`/projects/${recentActivity.project_id}`}>
                              {' '}
                              {recentActivity.project_name}
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='dash-section'>
          <div className='row justify-content-between'>
            <div className='col-12'>
              <div className='dash-section-title mb-8'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.EXPIRING_CONTRACTS'})}
              </div>
              {modules?.some(
                (module) => module.slug_name === 'gestao-de-contratos' && module.pivot.active
              ) ? (
                !isLoading && contractsData.length === 0 ? (
                  <ContractsPlaceholder />
                ) : (
                  <div className='table-responsive'>
                    <table
                      id='kt_file_manager_list'
                      data-kt-filemanager-table='folders'
                      className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer table-container'
                      {...getTableProps()}
                    >
                      <thead>
                        <tr className='text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0'>
                          {headers.map((column: ColumnInstance<Contracts>) => (
                            <CustomHeaderColumn key={column.id} column={column} />
                          ))}
                        </tr>
                      </thead>
                      <tbody className='fw-semibold text-gray-600' {...getTableBodyProps()}>
                        {rows.length > 0 ? (
                          rows.map((row: Row<Contracts>, i) => {
                            prepareRow(row)

                            return <CustomRow row={row} key={`row-${i}-${row.id}`} />
                          })
                        ) : (
                          <tr>
                            <td colSpan={7}>
                              <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                                {intl.formatMessage({id: 'TABLE.GENERAL.NO_RECORD_FOUND'})}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {isLoading && <Loading />}
                  </div>
                )
              ) : (
                <div className='d-flex justify-content-center'>
                  <UpgradePlanWidget />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      <ProjectCreateModalStepper
        show={showProjectModalStepper}
        handleClose={() => setProjectShowModalStepper(false)}
      />
      <TaskModal show={showTaskModalStepper} handleClose={() => setTaskShowModalStepper(false)} />

      {/* Modal para gerenciar calendários externos */}
      <ManageCalendarsModal
        show={showManageCalendarsModal}
        onClose={() => setShowManageCalendarsModal(false)}
        onSuccess={handleCalendarUpdated}
      />

      {/* Modal para criar tarefa a partir de evento externo */}
      <TaskModal
        show={showCreateTaskModal}
        handleClose={() => {
          setShowCreateTaskModal(false)
          setInitialTaskName('')
          setInitialTaskDate('')
        }}
        initialTaskName={initialTaskName}
        initialTaskDate={initialTaskDate}
      />
    </>
  )
}

export {DashboardManagerPage}
